import type { Api } from '@cdn/api';
import { Grants } from './grants';
import type { Handlers, Subscription, Transport } from './port';
import { Stream, type Check, type Listener } from './stream';

interface Specs {
	api: Api;
	/** `auto` follows the transport named by the realtime grant */
	adapter: 'auto' | 'supabase-broadcast' | 'polling';
	transports: { broadcast: Transport; polling: Transport };
	patience?: number;
	/** Looks at the page's session when a stream drops (`Renewal.check()`): a stream speaks only while signed */
	check?: Check;
}

/**
 * Opens and owns the realtime streams of the current scope. `reset()` closes every stream and forgets every
 * grant: it runs when the organization changes and when the account leaves, so no subscription of a previous
 * scope survives. `resume()` runs once the page's session was renewed and reconnects every stream that was held
 * while it was not signed.
 */
export class Realtime {
	#api: Api;
	#adapter: Specs['adapter'];
	#transports: Specs['transports'];
	#patience: number | undefined;
	#check: Check | undefined;
	#grants: Grants;
	#streams = new Set<Stream>();

	/** Number of open streams */
	get size() {
		return this.#streams.size;
	}

	constructor({ api, adapter, transports, patience, check }: Specs) {
		this.#api = api;
		this.#adapter = adapter;
		this.#transports = transports;
		this.#patience = patience;
		this.#check = check;
		this.#grants = new Grants(api);
	}

	/**
	 * @returns An opening stream; the caller closes it through `close()` or leaves it to `reset()`
	 */
	open(channel: string, listener: Listener): Stream {
		const stream: Stream = new Stream({ api: this.#api, channel, listener, patience: this.#patience, check: this.#check, connect: handlers => this.#connect(channel, handlers, stream) });
		this.#streams.add(stream);
		void stream.open();
		return stream;
	}

	close(stream: Stream) {
		stream.close();
		this.#grants.release(stream.channel);
		this.#streams.delete(stream);
	}

	/** The session was renewed: every stream held while it was not signed reconnects */
	resume() {
		for (const stream of this.#streams) stream.resume();
	}

	reset() {
		for (const stream of [...this.#streams]) this.close(stream);
		this.#grants.clear();
	}

	async #connect(channel: string, handlers: Handlers, stream: Stream): Promise<Subscription> {
		if (this.#adapter === 'polling') return this.#transports.polling.open(channel, handlers);

		let subscription: Subscription | undefined;
		const grant = await this.#grants.obtain(
			channel,
			renewed => (renewed.denied?.includes(channel) ? stream.revoke('denied') : subscription?.authorize?.(renewed.token)),
			() => stream.revoke('denied')
		);
		if (grant.denied?.includes(channel) || !grant.channels.includes(channel)) {
			stream.revoke('denied');
			return { close: () => undefined };
		}

		const broadcast = grant.transport.kind === 'supabase-broadcast' && grant.transport.url && grant.transport.key;
		subscription = (broadcast ? this.#transports.broadcast : this.#transports.polling).open(channel, handlers, grant);
		return subscription;
	}
}
